import { supabase } from '../lib/supabase';

/**
 * Product Service - Handles all product-related database operations
 * Database schema: products table with snake_case columns
 * React uses camelCase - this service handles conversion
 */

export const productService = {
  /**
   * Fetch all active products from database
   * Filters by is_active = true
   * @returns {Promise<Array>} Array of products in camelCase format
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase for React
      return data?.map(product => ({
        id: product?.id,
        title: product?.title,
        description: product?.description,
        price: parseFloat(product?.price),
        compareAtPrice: product?.compare_at_price ? parseFloat(product?.compare_at_price) : null,
        images: product?.images || [],
        imageUrls: product?.image_urls || [],
        category: product?.category,
        brand: product?.brand || null,
        sku: product?.sku || null,
        weight: product?.weight ? parseFloat(product?.weight) : null,
        dimensions: product?.dimensions || null,
        technicalSpecs: product?.technical_specs || {},
        stockQuantity: product?.stock_quantity || 0,
        isActive: product?.is_active,
        isFeatured: product?.is_featured || false,
        createdAt: product?.created_at,
        purchasePrice: product?.purchase_price ? parseFloat(product?.purchase_price) : 0,
        vatRate: product?.vat_rate ? parseFloat(product?.vat_rate) : 20,
        // Computed properties
        image: product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        primaryImage: product?.image_urls?.find(img => img?.isPrimary)?.url || product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        inStock: (product?.stock_quantity || 0) > 0,
        variants: product?.variants || null,
        // Bundle fields
        is_bundle: product?.is_bundle || false,
        isBundle: product?.is_bundle || false,
        bundleItems: product?.bundle_items || null,
        bundle_items: product?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(product?.bundle_discount_percentage) || 0,
        bundle_discount_percentage: parseFloat(product?.bundle_discount_percentage) || 0,
        // Sale badge logic
        badge: product?.compare_at_price && parseFloat(product?.compare_at_price) > parseFloat(product?.price) ? 'İndirim' : null
      }));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Fetch products filtered by category
   * @param {string} category - Category to filter by
   * @returns {Promise<Array>} Filtered products
   */
  async getByCategory(category) {
    try {
      if (!category || category === 'all') {
        return await this.getAll();
      }

      const { data, error } = await supabase?.from('products')?.select('*')?.eq('category', category)?.eq('is_active', true)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(product => ({
        id: product?.id,
        title: product?.title,
        description: product?.description,
        price: parseFloat(product?.price),
        compareAtPrice: product?.compare_at_price ? parseFloat(product?.compare_at_price) : null,
        images: product?.images || [],
        imageUrls: product?.image_urls || [],
        category: product?.category,
        brand: product?.brand || null,
        sku: product?.sku || null,
        weight: product?.weight ? parseFloat(product?.weight) : null,
        dimensions: product?.dimensions || null,
        technicalSpecs: product?.technical_specs || {},
        stockQuantity: product?.stock_quantity || 0,
        isActive: product?.is_active,
        isFeatured: product?.is_featured || false,
        createdAt: product?.created_at,
        purchasePrice: product?.purchase_price ? parseFloat(product?.purchase_price) : 0,
        vatRate: product?.vat_rate ? parseFloat(product?.vat_rate) : 20,
        image: product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        primaryImage: product?.image_urls?.find(img => img?.isPrimary)?.url || product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        inStock: (product?.stock_quantity || 0) > 0,
        variants: product?.variants || null,
        is_bundle: product?.is_bundle || false,
        isBundle: product?.is_bundle || false,
        bundleItems: product?.bundle_items || null,
        bundle_items: product?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(product?.bundle_discount_percentage) || 0,
        bundle_discount_percentage: parseFloat(product?.bundle_discount_percentage) || 0,
        badge: product?.compare_at_price && parseFloat(product?.compare_at_price) > parseFloat(product?.price) ? 'İndirim' : null
      }));
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  },

  /**
   * Search products by title
   * @param {string} searchQuery - Search term
   * @returns {Promise<Array>} Matching products
   */
  async search(searchQuery) {
    try {
      if (!searchQuery) {
        return await this.getAll();
      }

      const { data, error } = await supabase?.from('products')?.select('*')?.ilike('title', `%${searchQuery}%`)?.eq('is_active', true)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(product => ({
        id: product?.id,
        title: product?.title,
        description: product?.description,
        price: parseFloat(product?.price),
        compareAtPrice: product?.compare_at_price ? parseFloat(product?.compare_at_price) : null,
        images: product?.images || [],
        imageUrls: product?.image_urls || [],
        category: product?.category,
        brand: product?.brand || null,
        sku: product?.sku || null,
        weight: product?.weight ? parseFloat(product?.weight) : null,
        dimensions: product?.dimensions || null,
        technicalSpecs: product?.technical_specs || {},
        stockQuantity: product?.stock_quantity || 0,
        isActive: product?.is_active,
        isFeatured: product?.is_featured || false,
        createdAt: product?.created_at,
        image: product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        primaryImage: product?.image_urls?.find(img => img?.isPrimary)?.url || product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        inStock: (product?.stock_quantity || 0) > 0,
        variants: product?.variants || null,
        is_bundle: product?.is_bundle || false,
        isBundle: product?.is_bundle || false,
        bundleItems: product?.bundle_items || null,
        bundle_items: product?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(product?.bundle_discount_percentage) || 0,
        bundle_discount_percentage: parseFloat(product?.bundle_discount_percentage) || 0,
        badge: product?.compare_at_price && parseFloat(product?.compare_at_price) > parseFloat(product?.price) ? 'İndirim' : null
      }));
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  /**
   * Get products with advanced filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.category - Category filter
   * @param {string} filters.searchQuery - Search term
   * @param {Array} filters.priceRange - [min, max] price range
   * @param {number} filters.minRating - Minimum average rating filter
   * @param {string} filters.sortBy - Sort option
   * @returns {Promise<Array>} Filtered and sorted products
   */
  async getFiltered(filters) {
    try {
      // First, get all products with basic filters
      let query = supabase?.from('products')?.select('*')?.eq('is_active', true);

      // Category filter
      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }

      // Search filter
      if (filters?.searchQuery) {
        query = query?.ilike('title', `%${filters?.searchQuery}%`);
      }

      // Price range filter
      if (filters?.priceRange) {
        query = query?.gte('price', filters?.priceRange?.[0])?.lte('price', filters?.priceRange?.[1]);
      }

      // Brand filter
      if (filters?.brands && filters?.brands?.length > 0) {
        query = query?.in('brand', filters?.brands);
      }

      // Sorting
      switch (filters?.sortBy) {
        case 'price-asc':
          query = query?.order('price', { ascending: true });
          break;
        case 'price-desc':
          query = query?.order('price', { ascending: false });
          break;
        case 'newest':
          query = query?.order('created_at', { ascending: false });
          break;
        default:
          query = query?.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get review statistics for all products if minRating filter is active
      let productsWithRatings = data;
      
      if (filters?.minRating) {
        // Fetch all product reviews in one query
        const { data: reviewsData, error: reviewsError } = await supabase
          ?.from('product_reviews')
          ?.select('product_id, rating')
          ?.eq('status', 'approved');

        if (reviewsError) throw reviewsError;

        // Calculate average rating for each product
        const productRatings = {};
        reviewsData?.forEach(review => {
          if (!productRatings?.[review?.product_id]) {
            productRatings[review?.product_id] = { total: 0, count: 0 };
          }
          productRatings[review?.product_id].total += review?.rating;
          productRatings[review?.product_id].count += 1;
        });

        // Filter products by minimum rating
        productsWithRatings = data?.filter(product => {
          const ratingData = productRatings?.[product?.id];
          if (!ratingData) return false; // No reviews = exclude if rating filter active
          const avgRating = ratingData?.total / ratingData?.count;
          return avgRating >= filters?.minRating;
        });
      }

      return productsWithRatings?.map(product => ({
        id: product?.id,
        title: product?.title,
        description: product?.description,
        price: parseFloat(product?.price),
        compareAtPrice: product?.compare_at_price ? parseFloat(product?.compare_at_price) : null,
        images: product?.images || [],
        imageUrls: product?.image_urls || [],
        category: product?.category,
        brand: product?.brand || null,
        sku: product?.sku || null,
        weight: product?.weight ? parseFloat(product?.weight) : null,
        dimensions: product?.dimensions || null,
        technicalSpecs: product?.technical_specs || {},
        stockQuantity: product?.stock_quantity || 0,
        isActive: product?.is_active,
        isFeatured: product?.is_featured || false,
        createdAt: product?.created_at,
        purchasePrice: product?.purchase_price ? parseFloat(product?.purchase_price) : 0,
        vatRate: product?.vat_rate ? parseFloat(product?.vat_rate) : 20,
        image: product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        primaryImage: product?.image_urls?.find(img => img?.isPrimary)?.url || product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        inStock: (product?.stock_quantity || 0) > 0,
        variants: product?.variants || null,
        is_bundle: product?.is_bundle || false,
        isBundle: product?.is_bundle || false,
        bundleItems: product?.bundle_items || null,
        bundle_items: product?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(product?.bundle_discount_percentage) || 0,
        bundle_discount_percentage: parseFloat(product?.bundle_discount_percentage) || 0,
        badge: product?.compare_at_price && parseFloat(product?.compare_at_price) > parseFloat(product?.price) ? 'İndirim' : null
      }));
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      throw error;
    }
  },

  /**
   * Get all unique brands from active products
   * @returns {Promise<Array>} Array of unique brand names
   */
  async getBrands() {
    try {
      const { data, error } = await supabase
        ?.from('products')
        ?.select('brand')
        ?.eq('is_active', true)
        ?.not('brand', 'is', null);

      if (error) throw error;

      // Extract unique brands and sort alphabetically
      const uniqueBrands = [...new Set(data?.map(p => p?.brand)?.filter(Boolean))];
      return uniqueBrands?.sort();
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  },

  /**
   * Get single product by ID
   * @param {string} productId - Product UUID
   * @returns {Promise<Object>} Product details
   */
  async getById(productId) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('id', productId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        price: parseFloat(data?.price),
        compareAtPrice: data?.compare_at_price ? parseFloat(data?.compare_at_price) : null,
        images: data?.images || [],
        imageUrls: data?.image_urls || [],
        category: data?.category,
        brand: data?.brand || null,
        sku: data?.sku || null,
        weight: data?.weight ? parseFloat(data?.weight) : null,
        dimensions: data?.dimensions || null,
        technicalSpecs: data?.technical_specs || {},
        stockQuantity: data?.stock_quantity || 0,
        isActive: data?.is_active,
        isFeatured: data?.is_featured || false,
        createdAt: data?.created_at,
        image: data?.image_urls?.[0]?.url || data?.images?.[0] || null,
        primaryImage: data?.image_urls?.find(img => img?.isPrimary)?.url || data?.image_urls?.[0]?.url || data?.images?.[0] || null,
        inStock: (data?.stock_quantity || 0) > 0,
        variants: data?.variants || null,
        badge: data?.compare_at_price && parseFloat(data?.compare_at_price) > parseFloat(data?.price) ? 'İndirim' : null
      };
    } catch (error) {
      console.error('Error fetching product by ID:', error);
      throw error;
    }
  },

  /**
   * Admin: Create new product
   * @param {Object} productData - Product data in camelCase
   * @returns {Promise<Object>} Created product
   */
  async create(productData) {
    try {
      const { data, error } = await supabase
        ?.from('products')
        ?.insert([{
          title: productData?.title,
          description: productData?.description,
          price: productData?.price,
          compare_at_price: productData?.compareAtPrice || null,
          images: productData?.images || [],
          image_urls: productData?.imageUrls || [],
          category: productData?.category,
          brand: productData?.brand || null,
          sku: productData?.sku || null,
          weight: productData?.weight || null,
          dimensions: productData?.dimensions || null,
          technical_specs: productData?.technicalSpecs || {},
          stock_quantity: productData?.stockQuantity || 0,
          low_stock_threshold: productData?.lowStockThreshold || 10,
          track_inventory: productData?.trackInventory !== false,
          is_active: productData?.isActive !== false,
          is_featured: productData?.isFeatured || false,
          purchase_price: parseFloat(productData?.purchasePrice) || 0,
          vat_rate: parseFloat(productData?.vatRate) || 20,
          variants: productData?.variants || null,
          is_bundle: productData?.isBundle || false,
          bundle_items: productData?.bundleItems || null,
          bundle_discount_percentage: parseFloat(productData?.bundleDiscountPercentage) || 0
        }])
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        price: parseFloat(data?.price),
        compareAtPrice: data?.compare_at_price ? parseFloat(data?.compare_at_price) : null,
        images: data?.images || [],
        imageUrls: data?.image_urls || [],
        category: data?.category,
        brand: data?.brand || null,
        sku: data?.sku || null,
        weight: data?.weight ? parseFloat(data?.weight) : null,
        dimensions: data?.dimensions || null,
        technicalSpecs: data?.technical_specs || {},
        stockQuantity: data?.stock_quantity || 0,
        lowStockThreshold: data?.low_stock_threshold || 10,
        trackInventory: data?.track_inventory !== false,
        isActive: data?.is_active,
        isFeatured: data?.is_featured || false,
        createdAt: data?.created_at,
        purchasePrice: data?.purchase_price ? parseFloat(data?.purchase_price) : 0,
        vatRate: data?.vat_rate ? parseFloat(data?.vat_rate) : 20,
        variants: data?.variants || null,
        isBundle: data?.is_bundle || false,
        bundleItems: data?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(data?.bundle_discount_percentage) || 0
      };
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  /**
   * Admin: Update existing product
   * @param {string} productId - Product UUID
   * @param {Object} productData - Updated product data
   * @returns {Promise<Object>} Updated product
   */
  async update(productId, productData) {
    try {
      const { data, error } = await supabase
        ?.from('products')
        ?.update({
          title: productData?.title,
          description: productData?.description,
          price: productData?.price,
          compare_at_price: productData?.compareAtPrice || null,
          images: productData?.images || [],
          image_urls: productData?.imageUrls || [],
          category: productData?.category,
          brand: productData?.brand || null,
          sku: productData?.sku || null,
          weight: productData?.weight || null,
          dimensions: productData?.dimensions || null,
          technical_specs: productData?.technicalSpecs || {},
          stock_quantity: productData?.stockQuantity || 0,
          low_stock_threshold: productData?.lowStockThreshold || 10,
          track_inventory: productData?.trackInventory !== false,
          is_active: productData?.isActive !== false,
          is_featured: productData?.isFeatured || false,
          purchase_price: parseFloat(productData?.purchasePrice) || 0,
          vat_rate: parseFloat(productData?.vatRate) || 20,
          variants: productData?.variants || null,
          is_bundle: productData?.isBundle || false,
          bundle_items: productData?.bundleItems || null,
          bundle_discount_percentage: parseFloat(productData?.bundleDiscountPercentage) || 0
        })
        ?.eq('id', productId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        price: parseFloat(data?.price),
        compareAtPrice: data?.compare_at_price ? parseFloat(data?.compare_at_price) : null,
        images: data?.images || [],
        imageUrls: data?.image_urls || [],
        category: data?.category,
        brand: data?.brand || null,
        sku: data?.sku || null,
        weight: data?.weight ? parseFloat(data?.weight) : null,
        dimensions: data?.dimensions || null,
        technicalSpecs: data?.technical_specs || {},
        stockQuantity: data?.stock_quantity || 0,
        lowStockThreshold: data?.low_stock_threshold || 10,
        trackInventory: data?.track_inventory !== false,
        isActive: data?.is_active,
        isFeatured: data?.is_featured || false,
        createdAt: data?.created_at,
        purchasePrice: data?.purchase_price ? parseFloat(data?.purchase_price) : 0,
        vatRate: data?.vat_rate ? parseFloat(data?.vat_rate) : 20,
        variants: data?.variants || null,
        isBundle: data?.is_bundle || false,
        bundleItems: data?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(data?.bundle_discount_percentage) || 0
      };
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  /**
   * Admin: Delete product (soft delete by setting is_active = false)
   * @param {string} productId - Product UUID
   * @returns {Promise<boolean>} Success status
   */
  async delete(productId) {
    try {
      // 1. Fetch product data to get image URLs before deleting
      const { data: product, error: fetchError } = await supabase
        ?.from('products')
        ?.select('images, image_urls')
        ?.eq('id', productId)
        ?.single();

      if (fetchError) {
        console.error('Error fetching product for deletion:', fetchError);
      }

      // 2. Delete images from Supabase Storage
      if (product) {
        const storageFilePaths = [];

        // Collect paths from image_urls (structured objects with url field)
        if (product?.image_urls && Array.isArray(product?.image_urls)) {
          product?.image_urls?.forEach(imgObj => {
            const url = typeof imgObj === 'string' ? imgObj : imgObj?.url;
            if (url) {
              // Extract storage path from public URL
              // URL format: .../storage/v1/object/public/product-images/PATH
              const match = url?.match(/product-images\/(.+)/);
              if (match) {
                storageFilePaths?.push(match?.[1]);
              }
            }
          });
        }

        // Collect paths from images array (plain URL strings)
        if (product?.images && Array.isArray(product?.images)) {
          product?.images?.forEach(url => {
            if (url && typeof url === 'string') {
              const match = url?.match(/product-images\/(.+)/);
              if (match) {
                // Avoid duplicates
                if (!storageFilePaths?.includes(match?.[1])) {
                  storageFilePaths?.push(match?.[1]);
                }
              }
            }
          });
        }

        // Delete files from storage if any found
        if (storageFilePaths?.length > 0) {
          const { error: storageError } = await supabase
            ?.storage
            ?.from('product-images')
            ?.remove(storageFilePaths);

          if (storageError) {
            console.error('Error deleting product images from storage:', storageError);
            // Continue with product deletion even if image deletion fails
          }
        }
      }

      // 3. Soft delete the product (set is_active = false) to preserve order history
      // Hard delete fails when order_items reference this product (FK constraint)
      const { error } = await supabase
        ?.from('products')
        ?.update({ is_active: false })
        ?.eq('id', productId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  /**
   * Admin: Permanently delete product
   * @param {string} productId - Product UUID
   * @returns {Promise<boolean>} Success status
   */
  async hardDelete(productId) {
    try {
      const { error } = await supabase
        ?.from('products')
        ?.delete()
        ?.eq('id', productId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error hard deleting product:', error);
      throw error;
    }
  },

  /**
   * Admin: Get all products including inactive
   * @returns {Promise<Array>} All products
   */
  async getAllAdmin() {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(product => ({
        id: product?.id,
        title: product?.title,
        description: product?.description,
        price: parseFloat(product?.price),
        compareAtPrice: product?.compare_at_price ? parseFloat(product?.compare_at_price) : null,
        images: product?.images || [],
        imageUrls: product?.image_urls || [],
        category: product?.category,
        brand: product?.brand || null,
        sku: product?.sku || null,
        weight: product?.weight ? parseFloat(product?.weight) : null,
        dimensions: product?.dimensions || null,
        technicalSpecs: product?.technical_specs || {},
        stockQuantity: product?.stock_quantity || 0,
        lowStockThreshold: product?.low_stock_threshold || 10,
        trackInventory: product?.track_inventory !== false,
        isActive: product?.is_active,
        isFeatured: product?.is_featured || false,
        createdAt: product?.created_at,
        image: product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        primaryImage: product?.image_urls?.find(img => img?.isPrimary)?.url || product?.image_urls?.[0]?.url || product?.images?.[0] || null,
        inStock: (product?.stock_quantity || 0) > 0,
        variants: product?.variants || null,
        badge: product?.compare_at_price && parseFloat(product?.compare_at_price) > parseFloat(product?.price) ? 'İndirim' : null,
        isBundle: product?.is_bundle || false,
        bundleItems: product?.bundle_items || null,
        bundleDiscountPercentage: parseFloat(product?.bundle_discount_percentage) || 0
      }));
    } catch (error) {
      console.error('Error fetching all products:', error);
      throw error;
    }
  }
};